import React from "react";
import styles from "../../DynamicForm.module.scss";
import { renderStandardInput } from "./StandardInputRenderer";
import { createSerialKey, cleanValue } from "../../utils/formHelpers";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * Tile representation for form elements
 */
const TileRepresentation = ({ items, step, stepIndex, itemIndex }) => {
    
    const handleTileClick = (e, item) => {
        const input = e.currentTarget.querySelector('input');
        
        // Guard clause if no input found
        if (!input) return;
        
        const isRadio = input.type === 'radio';
        const isCheckbox = input.type === 'checkbox';
        
        if (isRadio) {
            // For radio buttons, first set this one to checked
            input.checked = true;
            e.currentTarget.classList.add(styles.active);
            
            // Then remove active class from all other radios in the same group
            const radioGroup = document.getElementsByName(input.name);
            radioGroup.forEach(radio => {
                // Skip the current radio button
                if (radio === input) return;
                
                // Find the parent tile and update its class
                const parentTile = radio.closest(`.${styles.tile}`);
                if (parentTile) {
                    parentTile.classList.remove(styles.active);
                }
                // Uncheck other radios
                radio.checked = false;
            });
        } else if (isCheckbox) {
            // Toggle the checked state
            input.checked = !input.checked;
            
            // Update active class based on the new state
            if (input.checked) {
                e.currentTarget.classList.add(styles.active);
            } else {
                e.currentTarget.classList.remove(styles.active);
            }
        }
        
        // Manually trigger change event
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
    };
    
    // Split the items into tile items and standard items
    const tileItems = items.filter(item => item.type === 9 || item.type === 10);
    const standardItems = items.filter(item => item.type !== 9 && item.type !== 10);
    
    // Group the radio buttons by name
    const radioGroups = {};
    tileItems.forEach(item => {
        if (item.type === 10 && item.required) {
            const name = `multi-${stepIndex}-${itemIndex}`;
            if (!radioGroups[name]) {
                radioGroups[name] = true;
            }
        }
    });
    
    return (
        <>
            <div className={`${styles.tileContainer} row g-3`}>
                {tileItems.map((item, idx) => {
                    // For radio required fields: Set the required attribute on at least one element per group
                    const isFirstRequiredRadio = item.type === 10 && item.required && 
                        !tileItems.slice(0, idx).some(prevItem => 
                            prevItem.type === 10 && 
                            `multi-${stepIndex}-${itemIndex}` === `multi-${stepIndex}-${prevItem.itemIndex}`
                        );
                    
                    // Clean the label from unwanted characters
                    const cleanedLabel = cleanValue(item.label);
                    
                    return (
                    <div 
                        key={idx} 
                        className="col-md-6 col-lg-4">
                        <div 
                            className={`${styles.tile} card p-0 cursor-pointer h-100 ${item.defaultChecked ? styles.active : ''}`}
                            onClick={(e) => handleTileClick(e, item)}
                            tabIndex={0} 
                            role={item.type === 10 ? "radio" : item.type === 9 ? "checkbox" : "button"}
                            aria-checked={item.defaultChecked ? "true" : "false"}
                            title={cleanedLabel}>
                            <div className="d-flex flex-column h-100">
                                {/* Image if available */}
                                {item.image && (
                                    <div className="tile-image-container">
                                        <img 
                                            src={item.image} 
                                            alt={cleanedLabel} 
                                            className={styles.tileImage} />
                                    </div>
                                )}
                                
                                {/* Two-part container for text and check icon */}
                                <div className="card-body d-flex align-items-center p-3">
                                    {/* Text content - with hyphenation */}
                                    <div 
                                        className={`tile-content ${styles.tileText} text-center flex-grow-1`}
                                        lang="de">
                                        {item.type === 9 || item.type === 10 ? (
                                            <>
                                                <input
                                                    className={styles.hiddenInput}
                                                    type={item.type === 9 ? "checkbox" : "radio"}
                                                    id={`${step.title}.${item.label}`}
                                                    name={
                                                        item.type === 10
                                                            ? `radio_group_${stepIndex}` 
                                                            : `${step.title}.${item.label}`
                                                    }
                                                    required={isFirstRequiredRadio || !!item.required}
                                                    data-serial-key={createSerialKey(step, item.label, idx)}
                                                    aria-invalid="false"
                                                    value={item.value || item.label}
                                                    onChange={() => {}}
                                                    defaultChecked={item.defaultChecked} />

                                                <div lang="de">
                                                    <strong>{cleanValue(item.label)}</strong>
                                                    {isFirstRequiredRadio === 1 && <span className="ms-1 text-danger">*</span>}
                                                    {item.description && (
                                                        <div className="form-text mt-1 small" lang="de">
                                                            {item.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <div lang="de">
                                                <strong>{cleanedLabel}</strong>
                                                {item.description && (
                                                    <div className="form-text small" lang="de">
                                                        {item.description}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Check icon container */}
                                    <div className={styles.checkIconContainer}>
                                        <FontAwesomeIcon 
                                            icon={faCheck} 
                                            className={styles.checkIcon}
                                            aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )})}
            </div>
            
            {/* Standard items for all other field types */}
            {standardItems.length > 0 && (
                <div className="mt-4">
                    {standardItems.map((item, idx) => {
                        // Add data-selection-item for SelectionCounter
                        const itemWithSelection = {
                            ...item,
                            "data-selection-item": "true"
                        };
                        
                        return renderStandardInput(itemWithSelection, step, idx, {
                            key: `standard-${idx}`,
                            wrapperClassName: "mb-3"
                        });
                    })}
                </div>
            )}
        </>
    );
};

export default TileRepresentation;
